import React, { memo, useCallback } from 'react';

import { useSectionCommands } from '../../commands/CustomizationCommandsContext';
import type { SectionViewModel } from '../../viewmodels/createSectionViewModel';
import { SortableRow, type SortableRowSlots } from '../dnd/SortableRow';
import { QuestionTable } from '../questions/QuestionTable';
import { SectionHeader } from './SectionHeader';

import styles from './SectionAccordion.scss';

type Props = {
  viewModel: SectionViewModel;
};

type ContentProps = Props & {
  sortable?: SortableRowSlots;
};

function SectionAccordionContent({ viewModel, sortable }: ContentProps) {
  const { id } = viewModel;
  const { register } = useSectionCommands();
  const sortableRef = sortable?.rowRef;

  // A stable ref keeps the drag library from re-measuring the section on every render
  const handleRef = useCallback(
    (element: HTMLElement | null) => {
      register(id, element);
      sortableRef?.(element);
    },
    [register, id, sortableRef]
  );

  return (
    <section
      // Deep-link target only; navigation resolves sections through the registry.
      id={`section-${id}`}
      ref={handleRef}
      style={sortable?.rowStyle}
      className={styles.accordion}
      data-test="section"
    >
      <SectionHeader viewModel={viewModel} dragHandle={sortable?.dragHandle} />
      {viewModel.isExpanded && (
        <QuestionTable viewModel={viewModel} sectionId={id} />
      )}
    </section>
  );
}

export const SectionAccordion = memo(function SectionAccordion(props: Props) {
  const { id, reorder } = props.viewModel;

  if (!reorder.canReorder) {
    return <SectionAccordionContent {...props} />;
  }

  return (
    <SortableRow id={id} reorder={reorder}>
      {sortable => <SectionAccordionContent {...props} sortable={sortable} />}
    </SortableRow>
  );
});
